import { Fragment, useEffect, useId, useState, type MouseEvent } from "react";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Search from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useTranslation } from "react-i18next";
import z from "zod";

import { Base64FileUpload, decodeBase64 } from "@thalamos/frontend";
import type { ExtendedVisionUser } from "@thalamos/vision-common";
import { tk, UserOrganisationMembershipSchema } from "@thalamos/vision-common";

import api, { apiHooks } from "../../../api/index.js";
import { useToastNotifications } from "../../../hooks/useToastNotifications.js";
import { PopupDialog } from "../../Dialog/PopupDialog.js";
import { TypographyI18N } from "../../TypographyI18N/TypographyI18N.js";
import { DeleteUserModal } from "./DeleteUserModal.js";
import { EditUserMembershipModal } from "./EditUserMembershipModal.js";
import { EditUserProfileModal } from "./EditUserProfileModal.js";

const debug = debugFn("vision-frontend:UsersManagement:UserRow");

type BulkCreateUserModalProps = {
  open: boolean;
  onClose: () => void;
};

export function BulkCreateUserModal({
  open,
  onClose,
}: BulkCreateUserModalProps) {
  return <BulkCreateUserModalInner open={open} onClose={onClose} />;
}

type BulkCreateUserModalInnerProps = BulkCreateUserModalProps;

export function BulkCreateUserModalInner({
  open,
  onClose,
}: BulkCreateUserModalInnerProps) {
  const { t } = useTranslation();
  const notifications = useToastNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [query, setQuery] = useState("");
  const [{ data: searchResults, loading }] = apiHooks.organisation.search(
    query,
    100,
  );

  const organisations = searchResults?.results || [];
  const [selectedOrg, setSelectedOrg] = useState<
    (typeof organisations)[0] | null
  >(null);

  type BulkCreateDryRunResult = {
    csvData: string;
    [key: string]: unknown;
  };

  const [dryRunResult, setDryRunResult] =
    useState<BulkCreateDryRunResult | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedOrg(null);
      setQuery("");
      setDryRunResult(null);
      setShowConfirmDialog(false);
    }
  }, [open]);

  if (loading && organisations.length === 0) {
    return <CircularProgress />;
  }

  const handleConfirmCreate = async (csvData: string) => {
    if (!selectedOrg) return;
    setIsSubmitting(true);

    try {
      const response = await api.user.bulkCreate(
        csvData,
        selectedOrg.id,
        false,
      );
      if (response.status !== 204) {
        notifications.show(t("admin.users.errorBulkCreate"), {
          severity: "error",
        });
      } else {
        notifications.show(t("admin.users.successBulkCreate"), {
          severity: "success",
        });
        onClose();
      }
    } catch (_err) {
      notifications.show(t("admin.users.errorBulkCreate"), {
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
      setDryRunResult(null);
      setShowConfirmDialog(false);
    }
  };

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableDialogRootPadding
    >
      <DialogTitle>{t("admin.users.bulkCreate")}</DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Stack spacing={3}>
          <FormControl fullWidth size="small">
            <FormLabel
              sx={{
                marginBottom: "0.5em",
                fontWeight: "bold",
                color: "text.primary",
              }}
            >
              {t("admin.users.organisationId")}
            </FormLabel>
            <Autocomplete
              loading={loading}
              options={organisations}
              getOptionLabel={(option) => option.name || ""}
              value={selectedOrg}
              onChange={(_, newValue) => {
                setSelectedOrg(newValue);
              }}
              onInputChange={(_, newInputValue) => {
                setQuery(newInputValue);
                if (!newInputValue) setSelectedOrg(null);
              }}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("common.search")}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>

          <Base64FileUpload
            accept=".csv"
            onDone={(uploadedFiles) => {
              void (async () => {
                if (!selectedOrg) {
                  notifications.show(t("admin.users.errorNoOrganisation"), {
                    severity: "error",
                  });
                  return;
                }

                if (isSubmitting) return;
                setIsSubmitting(true);

                try {
                  const uploadedFile =
                    uploadedFiles.length === 1 ? uploadedFiles[0] : undefined;
                  if (uploadedFile !== undefined) {
                    const base64Data = uploadedFile.base64;
                    const csvData =
                      typeof base64Data === "string"
                        ? decodeBase64(base64Data.split(",")[1] ?? "")
                        : "";

                    const dryRunResponse = await api.user.bulkCreate(
                      csvData,
                      selectedOrg.id,
                      true,
                    );

                    setDryRunResult(dryRunResponse.data);
                    setShowConfirmDialog(true);
                  }
                } catch (_err) {
                  notifications.show(t("admin.users.errorBulkCreate"), {
                    severity: "error",
                  });
                } finally {
                  setIsSubmitting(false);
                }
              })();
            }}
            label={t("admin.users.uploadCsv")}
            disabled={isSubmitting || !selectedOrg}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          <TypographyI18N translationKey={tk("common.close")} />
        </Button>
      </DialogActions>
      {showConfirmDialog && dryRunResult && (
        <PopupDialog
          open
          onClose={() => {
            setShowConfirmDialog(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("admin.users.bulkCreateConfirm")}</DialogTitle>
          <DialogContent>
            <pre>
              {JSON.stringify({ ...dryRunResult, csvData: undefined }, null, 2)}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowConfirmDialog(false);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => {
                void handleConfirmCreate(dryRunResult.csvData);
              }}
              color="primary"
              disabled={isSubmitting}
            >
              {t("common.confirm")}
            </Button>
          </DialogActions>
        </PopupDialog>
      )}
    </PopupDialog>
  );
}

function UserRow({
  user,
  refetchUsers,
  bgColor,
}: {
  user: ExtendedVisionUser;
  bgColor: string;
  refetchUsers: ReturnType<typeof apiHooks.user.extendedSearch>[1];
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const notifications = useToastNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showEditUserMembershipModal, setShowEditUserMembershipModal] =
    useState(false);
  const [showEditUserProfileModal, setShowEditUserProfileModal] =
    useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const { email, id, name, shoulderNumber, memberships } = user;
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const organisations = Array.from(
    new Set(memberships.map((membership) => membership.organisationName)),
  );

  const roles = Array.from(
    new Set(memberships.map((membership) => membership.role)),
  );

  return (
    <>
      <TableRow
        key={id}
        sx={{
          backgroundColor: bgColor,
        }}
      >
        <TableCell>{name}</TableCell>
        <TableCell>{email}</TableCell>
        <TableCell>{shoulderNumber}</TableCell>
        <TableCell>
          {organisations.map((organisation, index) => (
            <Fragment key={index}>
              {organisation}
              {index < organisations.length - 1 && <br />}
            </Fragment>
          ))}
        </TableCell>
        <TableCell>
          {roles.map((role, index) => (
            <Fragment key={index}>
              {role}
              {index < roles.length - 1 && <br />}
            </Fragment>
          ))}
        </TableCell>
        <TableCell>
          <Button variant="outlined" onClick={handleClick}>
            {t("admin.incidents.action")}
          </Button>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
              onClick={() => {
                setShowEditUserProfileModal(true);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              {t("admin.users.editProfile")}
            </MenuItem>
            <MenuItem
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
              onClick={() => {
                setShowEditUserMembershipModal(true);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              {t("admin.users.editMemeberships")}
            </MenuItem>
            <MenuItem
              sx={{
                color: theme.palette.error.main,
                fontWeight: 600,
              }}
              onClick={() => {
                setShowDeleteUserModal(true);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              {t("admin.users.deleteUser")}
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      {showEditUserProfileModal && (
        <EditUserProfileModal
          open={showEditUserProfileModal}
          onClose={() => {
            setShowEditUserProfileModal(false);
          }}
          user={user}
          onConfirm={async (userId, updateUserData) => {
            try {
              const response = await api.user.updateUserProfile(
                userId,
                updateUserData,
              );

              if (response.status !== 204) {
                debug("API error updating user profile:", response.data);
                notifications.show(t("admin.users.apiUpdateError"), {
                  severity: "error",
                });
                return;
              } else {
                notifications.show(
                  t("admin.users.successMessage", {
                    userName: user.name,
                  }),
                  { severity: "success" },
                );
              }

              setShowEditUserProfileModal(false);

              void refetchUsers();
            } catch (error) {
              debug("Error updating user profile:", error);
              notifications.show(t("admin.users.updateExceptionError"), {
                severity: "error",
              });
            }
          }}
        />
      )}

      {showEditUserMembershipModal && (
        <EditUserMembershipModal
          open={showEditUserMembershipModal}
          onClose={() => {
            setShowEditUserMembershipModal(false);
          }}
          user={user}
          onConfirm={async (selectedRoles, org, supervisorSyncExemption) => {
            try {
              const memberships = Object.entries(selectedRoles)
                .filter(([_, selected]) => selected)
                .map(([role]) => {
                  const organisation = org[role];
                  if (organisation === undefined) {
                    // Abandon the whole update: this list replaces the user's
                    // memberships, so omitting one would silently revoke it.
                    throw new Error(
                      `No organisation selected for role ${role}`,
                    );
                  }
                  return {
                    organisationId: organisation.id,
                    organisationName: organisation.name,
                    organisationRole: organisation.role,
                    role: role,
                    managingOrganisationId: organisation.managingOrganisationId,
                    type: "organisation",
                  };
                });

              const parsed = z
                .array(UserOrganisationMembershipSchema)
                .safeParse(memberships);

              if (!parsed.success) {
                debug("Validation error:", parsed.error);
                notifications.show(t("admin.users.validationError"), {
                  severity: "error",
                });
                return;
              }

              const response = await api.user.setMemberships(
                user.id,
                parsed.data,
              );

              if (response.status !== 204) {
                debug("API error updating memberships:", response.data);
                notifications.show(t("admin.users.apiUpdateError"), {
                  severity: "error",
                });
                return;
              }

              // Update supervisor sync exemption if it changed
              if (supervisorSyncExemption !== user.supervisorSyncExemption) {
                const exemptionResponse =
                  await api.user.setSupervisorSyncExemption(user.id, {
                    exempt: supervisorSyncExemption,
                  });
                if (exemptionResponse.status !== 204) {
                  debug(
                    "API error updating supervisor sync exemption:",
                    exemptionResponse.data,
                  );
                  notifications.show(t("admin.users.apiUpdateError"), {
                    severity: "error",
                  });
                  return;
                }
              }

              notifications.show(
                t("admin.users.successMessage", {
                  userName: user.name,
                }),
                { severity: "success" },
              );

              setShowEditUserMembershipModal(false);

              void refetchUsers();
            } catch (error) {
              debug("Error updating user memberships:", error);
              notifications.show(t("admin.users.updateExceptionError"), {
                severity: "error",
              });
            }
          }}
        />
      )}

      {showDeleteUserModal && (
        <DeleteUserModal
          open={showDeleteUserModal}
          onClose={() => {
            setShowDeleteUserModal(false);
          }}
          user={user}
          onConfirm={async (userId) => {
            try {
              const response = await api.user.deleteUser(userId);
              if (response.status !== 204) {
                debug("API error deleting user:", response.data);
                notifications.show(t("admin.users.apiDeleteError"), {
                  severity: "error",
                });
                return;
              } else {
                notifications.show(
                  t("admin.users.successMessage", {
                    userName: user.name,
                  }),
                  { severity: "success" },
                );
              }

              setShowDeleteUserModal(false);

              void refetchUsers();
            } catch (error) {
              debug("Error deleting user:", error);
              notifications.show(t("admin.users.deleteExceptionError"), {
                severity: "error",
              });
            }
          }}
        />
      )}
    </>
  );
}

export const UsersInner = ({
  users,
  page,
  setPage,
  numPages,
  refetchUsers,
}: {
  users: ExtendedVisionUser[];
  page: number;
  setPage: (page: number) => void;
  numPages: number;
  refetchUsers: ReturnType<typeof apiHooks.user.extendedSearch>[1];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ verticalAlign: "bottom" }}>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.users.name")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.users.responsibleOfficerEmail")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.users.badgeNumber")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.users.organisation")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.users.role")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.users.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user: ExtendedVisionUser, index) => {
              return (
                <UserRow
                  bgColor={
                    index % 2 === 0 ? theme.palette.secondary.main : "white"
                  }
                  key={user.id}
                  user={user}
                  refetchUsers={refetchUsers}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingTop: "3em",
        }}
      >
        <button
          disabled={page === 0}
          onClick={() => {
            if (page === 0) {
              return;
            }
            setPage(page - 1);
          }}
        >
          &#60;
        </button>
        <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
          Page {page + 1} of {numPages}
        </Box>
        <button
          disabled={page >= numPages - 1}
          onClick={() => {
            if (page < numPages - 1) {
              setPage(page + 1);
            }
          }}
        >
          &#62;
        </button>
      </Box>
    </Box>
  );
};

export function UsersManagement() {
  const ariaId = useId();
  const [userFilterQuery, setUserFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);

  const userPageSize = 50;
  const [{ data: users }, refetchUsers] = apiHooks.user.extendedSearch(
    userFilterQuery,
    userPageSize,
    page * userPageSize,
  );
  const numPages =
    users && users.count ? Math.ceil(users.count / userPageSize) : 1;
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
          alignItems: "center",
        }}
      >
        <FormControl sx={{ marginBottom: "1em", marginLeft: "1em" }}>
          <FormLabel
            htmlFor={ariaId}
            sx={{
              marginBottom: "0.5em",
              fontWeight: "bold",
              color: "text.primary",
            }}
          >
            {t("admin.users.searchLabel")}
          </FormLabel>
          <TextField
            value={userFilterQuery}
            onChange={(e) => {
              setUserFilterQuery(e.target.value);
              setPage(0);
            }}
            slotProps={{
              input: {
                id: ariaId,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
        </FormControl>

        <Box sx={{ marginLeft: "2em" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowBulkCreateModal(true);
            }}
          >
            {t("admin.users.bulkCreate")}
          </Button>
        </Box>
      </Box>
      {!users ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <UsersInner
          users={users.results || []}
          page={page}
          setPage={setPage}
          numPages={numPages}
          refetchUsers={refetchUsers}
        />
      )}
      {showBulkCreateModal && (
        <BulkCreateUserModal
          open={showBulkCreateModal}
          onClose={() => {
            setShowBulkCreateModal(false);
          }}
        />
      )}
    </Box>
  );
}
